/**
 * Privacy tab in Settings — exposes the two user-facing GDPR rights:
 *   - Download my data (Article 15 access + Article 20 portability)
 *   - Delete my account (Article 17 erasure, with a 30-day grace window)
 *
 * The heavy lifting (data collection, redaction, purge scheduling) lives in
 * the backend `/api/privacy/*` endpoints; this component is intentionally
 * thin so the paper trail on the server is the single source of truth.
 */
import { useState } from 'react';
import { Download, Trash2, Loader2, ShieldCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';

export function PrivacyTab() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  // -------- Export --------
  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      const res = await api.exportMyData();
      const blob = new Blob([res.data], { type: 'application/json' });
      // Extract filename the backend suggested via Content-Disposition.
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `tune4tune-data-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  // -------- Delete --------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const CONFIRM_PHRASE = 'DELETE';

  const performDeletion = async () => {
    setDeleting(true);
    try {
      const { data } = await api.requestAccountDeletion();
      const when = data?.purge_at ? new Date(data.purge_at).toLocaleDateString() : 'in 30 days';
      toast.success(`Account scheduled for deletion on ${when}. You've been logged out.`);
      setConfirmOpen(false);
      // Log out immediately — the session cookie was already invalidated
      // server-side, but tearing down the auth state client-side means the
      // next page render happens as a signed-out visitor.
      try { await logout(); } catch (e) { /* auth cleanup best-effort */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not process your deletion request.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="privacy-tab">
      {/* Header explaining what this tab is */}
      <div className="rounded-xl border border-[#222] bg-[#0e0e0e] p-5 space-y-2">
        <div className="flex items-center gap-2 text-white">
          <ShieldCheck className="w-5 h-5 text-lime" />
          <h3 className="font-heading font-semibold text-lg">Your privacy rights</h3>
        </div>
        <p className="text-sm text-[#A1A1AA] leading-relaxed">
          Everything on this tab is protected under the GDPR and Florida
          consumer-privacy law. Review our{' '}
          <Link to="/privacy" className="text-lime hover:underline inline-flex items-center gap-1">
            Privacy Policy <ExternalLink className="w-3 h-3" />
          </Link>{' '}
          for full details.
        </p>
      </div>

      {/* -- Export -- */}
      <div className="rounded-xl border border-[#222] bg-[#0e0e0e] p-5 space-y-3" data-testid="privacy-export-card">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h4 className="font-heading font-semibold text-white">Download my data</h4>
            <p className="text-sm text-[#A1A1AA] leading-relaxed">
              Get a machine-readable JSON copy of everything we hold on you —
              profile, orders, messages, uploads, and login history. Useful
              for taking your data to another service, or just for your own
              records.
            </p>
          </div>
          <Button
            onClick={onExport}
            disabled={exporting}
            className="btn-primary shrink-0"
            data-testid="privacy-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? 'Preparing…' : 'Download JSON'}
          </Button>
        </div>
      </div>

      {/* -- Delete -- */}
      <div className="rounded-xl border border-red-900/40 bg-red-950/10 p-5 space-y-3" data-testid="privacy-delete-card">
        <div className="space-y-1">
          <h4 className="font-heading font-semibold text-red-400">Delete my account</h4>
          <p className="text-sm text-[#A1A1AA] leading-relaxed">
            This starts a permanent deletion. Your profile is redacted from
            public pages right away. After a 30-day recovery window, everything
            we can lawfully delete is purged. Tax records and completed order
            invoices are kept for 7 years to meet US IRS retention rules.
          </p>
          {isAdmin && (
            <p className="text-xs text-amber-400 mt-2">
              Admin accounts cannot self-delete — email hallandpull@gmail.com
              to arrange removal.
            </p>
          )}
        </div>
        <div>
          <Button
            variant="destructive"
            onClick={() => { setConfirmText(''); setConfirmOpen(true); }}
            disabled={isAdmin}
            data-testid="privacy-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />Delete my account
          </Button>
        </div>
      </div>

      {/* Confirm dialog. Typing DELETE is a UX best practice for irreversible
          destructive actions — it prevents muscle-memory misclicks. */}
      <AlertDialog open={confirmOpen} onOpenChange={(v) => !deleting && setConfirmOpen(v)}>
        <AlertDialogContent
          className="bg-surface border-[#262626] max-w-md"
          data-testid="privacy-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-[#A1A1AA] text-sm leading-relaxed space-y-3">
                <p>
                  This starts a 30-day countdown. Your profile is redacted
                  immediately and everything we can lawfully delete is purged
                  when the countdown ends.
                </p>
                <p>
                  To confirm, type{' '}
                  <code className="text-lime bg-black px-1.5 py-0.5 rounded">
                    {CONFIRM_PHRASE}
                  </code>{' '}
                  below.
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  className="bg-black border-[#333] text-white"
                  data-testid="privacy-delete-confirm-input"
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#333] bg-transparent text-white hover:bg-[#1a1a1a]"
              disabled={deleting}
              data-testid="privacy-delete-cancel-btn"
            >
              Keep my account
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              disabled={deleting || confirmText.trim() !== CONFIRM_PHRASE}
              onClick={(e) => { e.preventDefault(); performDeletion(); }}
              data-testid="privacy-delete-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
